import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { winepsApi } from "./api";
import type { DownloadPhoto } from "./photo-cache";

/** One photo surface: lazy metadata, foreground renewal and one image recovery. */
export function Photo({ token, objectId, kind, variant = "display", role = "label", alt,
  className, fallback, large = false,
}: {
  token: string; objectId: string; kind: "tasting" | "cellar";
  variant?: "original" | "display" | "thumbnail"; role?: "label" | "back_label";
  alt: string; className: string; fallback: ReactNode; large?: boolean;
}) {
  const host = useRef<HTMLSpanElement>(null);
  const [near, setNear] = useState(large);
  const [revision, setRevision] = useState(0);
  const [failure, setFailure] = useState(false);
  const [value, setValue] = useState<{ key: string; photos: DownloadPhoto[] }>();
  const automaticUsed = useRef(false);
  const key = `${token}:${kind}:${objectId}:${variant}`;

  useEffect(() => {
    if (large || !host.current || !("IntersectionObserver" in window)) { setNear(true); return; }
    const observer = new IntersectionObserver(([entry]) => setNear(entry.isIntersecting), {
      rootMargin: "300px",
    });
    observer.observe(host.current);
    return () => observer.disconnect();
  }, [large, key]);

  useEffect(() => { automaticUsed.current = false; }, [key]);
  const forceNext = useRef(false);
  useEffect(() => {
    if (!near) return;
    let active = true;
    setFailure(false);
    const forced = forceNext.current;
    forceNext.current = false;
    const pending = kind === "tasting"
      ? winepsApi.photos(token, objectId, variant, forced)
      : winepsApi.cellarDownloadPhotos(token, objectId, forced);
    void pending.then(photos => {
      if (active) setValue({ key, photos });
    }).catch(() => {
      if (active) { setValue(undefined); setFailure(true); }
    });
    return () => { active = false; };
  }, [key, kind, near, objectId, token, variant, revision]);

  const refresh = useCallback((force = false) => {
    forceNext.current = force;
    if (force) setValue(undefined);
    setRevision(current => current + 1);
  }, []);
  const photo = value?.key === key ? value.photos.find(item => item.role === role) : undefined;
  useEffect(() => {
    if (!near || !photo) return;
    const delay = Date.parse(photo.expires_at) - Date.now() - 30_000;
    // Malformed/short-lived metadata never creates a zero-delay renewal loop.
    if (!Number.isFinite(delay) || delay <= 0) return;
    const timer = window.setTimeout(() => refresh(), Math.min(delay, 2_147_483_647));
    return () => window.clearTimeout(timer);
  }, [near, photo, refresh]);
  useEffect(() => {
    const foreground = () => {
      if (near && document.visibilityState === "visible") refresh();
    };
    const online = () => { if (near && !photo) refresh(); };
    const invalidated = () => { if (near) refresh(); };
    document.addEventListener("visibilitychange", foreground);
    window.addEventListener("online", online);
    window.addEventListener("wineps:photos-invalidated", invalidated);
    return () => {
      document.removeEventListener("visibilitychange", foreground);
      window.removeEventListener("online", online);
      window.removeEventListener("wineps:photos-invalidated", invalidated);
    };
  }, [near, photo, refresh]);

  return <span ref={host} className="photo-slot">
    {photo && !failure ? <img className={className} src={photo.url} alt={alt}
      loading={large ? "eager" : "lazy"} onError={() => {
        if (!automaticUsed.current) { automaticUsed.current = true; refresh(true); }
        else setFailure(true);
      }}/> : fallback}
    {failure && <button type="button" className="photo-retry" aria-label={`Réessayer la photo : ${alt}`}
      onClick={event => {
        event.stopPropagation();
        automaticUsed.current = false;
        refresh(true);
      }}>Réessayer la photo</button>}
  </span>;
}
